import type { Column } from '@tanstack/react-table';
import { Check, PlusCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

interface FacetedFilterOption {
  label: string;
  value: string;
}

export interface FacetConfig {
  columnId: string;
  title: string;
  options: FacetedFilterOption[];
}

interface DataTableFacetedFilterProps<TData> {
  column: Column<TData>;
  title: string;
  options: FacetedFilterOption[];
}

export function DataTableFacetedFilter<TData>({
  column,
  title,
  options,
}: DataTableFacetedFilterProps<TData>) {
  const facets = column.getFacetedUniqueValues();
  const filterValue = column.getFilterValue();
  const selected = new Set<string>(Array.isArray(filterValue) ? filterValue : []);

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    const values = Array.from(next);
    column.setFilterValue(values.length ? values : undefined);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="border-dashed">
          <PlusCircle className="size-4" />
          {title}
          {selected.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="secondary" className="rounded-sm px-1 font-normal">
                {selected.size}
              </Badge>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48">
        {options.map((option) => {
          const isSelected = selected.has(option.value);
          return (
            <DropdownMenuItem
              key={option.value}
              onSelect={(event) => {
                event.preventDefault();
                toggle(option.value);
              }}
            >
              <span
                className={cn(
                  'flex size-4 items-center justify-center rounded-sm border border-[var(--color-primary)]',
                  isSelected
                    ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)]'
                    : 'opacity-50',
                )}
              >
                {isSelected && <Check className="size-3" />}
              </span>
              <span className="flex-1">{option.label}</span>
              <span className="text-xs text-[var(--color-muted-foreground)]">
                {facets.get(option.value) ?? 0}
              </span>
            </DropdownMenuItem>
          );
        })}
        {selected.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => column.setFilterValue(undefined)}
              className="justify-center"
            >
              Clear filters
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
